import React, { useEffect, useState } from 'react'
import { getNorthStarSnapshot, NorthStarPayload, NorthStarSnapshot } from '../../services/northstar'
import NorthStarChart, { UNATTRIBUTED_FILL } from './Chart'
import './northstar.css'

const pct = (v: number | null | undefined) =>
  v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`

/** Colour a growth figure against the target: hit / slowing / shrinking. */
const growthClass = (v: number | null | undefined, target: number) =>
  v == null ? '' : v >= target ? 'ns-g' : v >= 0 ? 'ns-a' : 'ns-r'

/**
 * The footer is authored in build_page.py and arrives with <b> and <br/> in it.
 * Rendered through a tiny whitelist rather than dangerouslySetInnerHTML — the
 * string is ours today, but it reaches this component through a database and an
 * ingest endpoint, and neither should be able to inject markup into the page.
 */
const RichNote: React.FC<{ html: string }> = ({ html }) => (
  <>
    {html.split(/<br\s*\/?>/).map((line, i) => (
      <div key={i}>
        {line.split(/<\/?b>/).map((part, j) =>
          j % 2 === 1 ? <strong key={j}>{part}</strong> : <span key={j}>{part}</span>,
        )}
      </div>
    ))}
  </>
)

const NorthStar: React.FC = () => {
  const [snapshot, setSnapshot] = useState<NorthStarSnapshot | null>(null)
  const [error, setError] = useState<string>('')
  const [loading, setLoading] = useState(true)

  const load = async () => {
    setLoading(true)
    setError('')
    try {
      setSnapshot(await getNorthStarSnapshot())
    } catch (e: any) {
      // 403 is the expected answer for a signed-in non-staff user, so say what
      // it means rather than showing a generic failure.
      setError(
        e?.response?.status === 403
          ? 'This dashboard is limited to staff accounts.'
          : 'Could not load the scoreboard. Try again in a moment.',
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  if (loading) return <div className='ns-page'><div className='ns-wrap'><div className='ns-msg'>Loading…</div></div></div>
  if (error) return <div className='ns-page'><div className='ns-wrap'><div className='ns-msg'>{error}</div></div></div>

  if (!snapshot?.available || !snapshot.payload) {
    return (
      <div className='ns-page'>
        <div className='ns-wrap'>
          <div className='ns-msg'>
            No scoreboard published yet. Run <code>./update.sh</code> in
            <code> pythons/North-Star</code> to publish the first snapshot.
          </div>
        </div>
      </div>
    )
  }

  const d: NorthStarPayload = snapshot.payload
  const s = d.stats
  const verdict =
    s.trailing >= d.target ? ['ns-bg-g', 'on track']
      : s.trailing >= d.target / 2 ? ['ns-bg-a', 'below target']
        : ['ns-bg-r', 'off track']

  const recent = d.rows.slice(-8).reverse()
  const hasUnattributed = d.rows.some((r) => d.channels.every((c) => r.band[c.key] == null))

  return (
    <div className='ns-page'>
      <div className='ns-wrap'>
        <div className='ns-header'>
          <div>
            <h1>{d.company} · North Star</h1>
            <div className='ns-sub'>
              {d.metric} · indexed to <b>100</b> at {d.baseline} · target <b>{d.target.toFixed(0)}%/week</b>
            </div>
          </div>
          <div className='ns-updated'>
            refreshed {snapshot.generated_at ? new Date(snapshot.generated_at).toLocaleDateString() : '—'}
            <br />
            {d.rows.length} weeks of data
          </div>
        </div>

        <div className='ns-cards'>
          <div className='ns-card'>
            <div className='ns-label'>Index now</div>
            <div className='ns-big'>{s.index.toFixed(0)}</div>
            <div className='ns-note'>{s.yoy == null ? '' : `YoY ${pct(s.yoy)}`}</div>
          </div>
          <div className='ns-card'>
            <div className='ns-label'>Trailing {d.trailing}-wk growth</div>
            <div className={`ns-big ${growthClass(s.trailing, d.target)}`}>{pct(s.trailing)}</div>
            <span className={`ns-chip ${verdict[0]}`}>{verdict[1]}</span>
          </div>
          <div className='ns-card'>
            <div className='ns-label'>This week WoW</div>
            <div className={`ns-big ${growthClass(s.wow, d.target)}`}>{pct(s.wow)}</div>
            <div className='ns-note'>target {d.target.toFixed(0)}%/week</div>
          </div>
          <div className='ns-card'>
            <div className='ns-label'>Paying subscribers</div>
            <div className='ns-big'>{s.subs.toLocaleString()}</div>
            <div className='ns-note'>
              {/* A stale channel is named ahead of the delta on purpose. The
                  headline counts that channel's last known figure while the
                  delta is like-for-like (it excludes it), so showing the delta
                  next to the total would advertise a change the number above
                  does not contain. */}
              {s.subsAsOf
                ? s.subsAsOf
                : s.subsDelta == null
                  ? 'across all channels'
                  : `${s.subsDelta >= 0 ? '+' : ''}${s.subsDelta} this week`}
            </div>
          </div>
          <div className='ns-card'>
            <div className='ns-label'>Streak ≥ {d.target.toFixed(0)}%</div>
            <div className='ns-big'>{s.streak}<span style={{ fontSize: 14, color: '#9aa6c7' }}> wk</span></div>
            <div className='ns-note'>consecutive weeks hitting target</div>
          </div>
        </div>

        <div className='ns-chcards'>
          {d.channels.map((c) => {
            const cs = d.channelStats[c.key]
            return (
              <div className='ns-card' key={c.key}>
                <div className='ns-label'>
                  <span className='ns-swatch' style={{ background: c.color }} />
                  {c.label}
                </div>
                {!cs?.known ? (
                  <>
                    <div className='ns-big' style={{ color: '#9aa6c7' }}>—</div>
                    <div className='ns-note'>not tracked yet</div>
                  </>
                ) : (
                  <>
                    <div className='ns-big'>{(cs.share ?? 0).toFixed(1)}%</div>
                    <div className='ns-note'>
                      {cs.subs == null ? 'share of MRR' : `${cs.subs.toLocaleString()} subscribers`}
                      {cs.wow == null ? '' : ` · ${pct(cs.wow)} WoW`}
                      {/* Its share is measured in its own latest week, so a stale
                          channel's card does not add to 100% with the others.
                          The date is what makes that legible rather than wrong. */}
                      {cs.stale ? ` · as of ${cs.asOf}` : ''}
                    </div>
                  </>
                )}
              </div>
            )
          })}
        </div>

        <div className='ns-panel'>
          <h2>Growth story — {d.metric} indexed to base 100, stacked by channel</h2>
          <p>
            100 = total MRR on {d.baseline}. The bands stack to that index, so the top of each
            column is the company and each fill is where it came from.
          </p>
          <NorthStarChart data={d} />
          <div className='ns-legend'>
            {d.channels.map((c) => (
              <span key={c.key}>
                <span className='ns-swatch' style={{ background: c.color }} />
                {c.label}
              </span>
            ))}
            {/* Keyed on whether a neutral column is actually drawn, not on
                mixStartIdx: after the backfill the earliest weeks are web-only,
                which is a one-channel breakdown and renders as a Web column.
                mixStartIdx is still non-zero there, so keying on it would name
                a colour that appears nowhere on the chart. */}
            {hasUnattributed ? (
              <span>
                <span className='ns-swatch' style={{ background: UNATTRIBUTED_FILL }} />
                total only — no channel breakdown yet
              </span>
            ) : null}
          </div>
        </div>

        <div className='ns-panel'>
          <h2>Last 8 weeks</h2>
          <p>
            Contribution = how many points of the week&apos;s total growth each channel supplied.
            The three add up to the WoW figure, so you can see which channel actually moved
            the number. A dash means that channel was not known that week — which is not the
            same as it earning nothing.
          </p>
          <table className='ns-table'>
            <thead>
              <tr>
                <th>Week</th>
                <th>Index</th>
                <th>WoW</th>
                {d.channels.map((c) => (
                  <th key={c.key}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recent.map((r) => (
                <tr key={r.week}>
                  <td>{r.week}</td>
                  <td>{r.index.toFixed(0)}</td>
                  <td className={growthClass(r.wow, d.target)}>{pct(r.wow)}</td>
                  {d.channels.map((c) => (
                    <td key={c.key}>{r.contrib[c.key] == null ? '—' : pct(r.contrib[c.key])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className='ns-footer'>
          <RichNote html={d.footer} />
        </div>
      </div>
    </div>
  )
}

export default NorthStar
